// NodeChecklistField.jsx — v2: one checklist field = one node-local list

import React, { useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Services
import {
  buildChecklistProgressSummary,
  ensureChecklistForField,
  subscribeToChecklistForField,
} from 'services/checklists/ChecklistService';
import { computeChecklistProgress } from 'services/checklists/checklistModels';

// Utilities
import { normalizeChecklistTemplateItems } from 'utilities/ChecklistTemplateItems';

// Hooks
import useChecklistInlineEditor from './hooks/useChecklistInlineEditor';

// CSS
import './NodeChecklistField.css';

// Components
import ChecklistAccordionPanel from './components/ChecklistAccordionPanel';

/**
 * NodeChecklistField
 *
 * Renders the single checklist list for a schema checklist field.
 * In readOnly / previewMode: show items + progress; no ensure, no writes,
 * no toggle / edit / add / reorder. Visitors without a live checklist doc
 * fall back to the denormalized node field summary (itemTexts + counts).
 *
 * @param {object} node - The node that owns the checklist field.
 * @param {string} fieldName - The field name used as the checklist anchor.
 * @param {string} fieldLabel - Display label for the field / list title.
 * @param {*} value - Denormalized progress summary on the node field.
 * @param {function} onChange - Writes a compact progress summary onto the node field.
 * @param {boolean} [readOnly=false] - Display-only.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 *
 * @returns {ReactNode} The rendered checklist field surface.
 */
const NodeChecklistField = ({
  node,
  fieldName,
  fieldLabel,
  field = null,
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {
  const { theme } = useTheme();
  const { user } = useUser();
  const { activeBase } = useBase();
  const isReadOnly = Boolean(readOnly || previewMode);
  const schemaField = field && typeof field === 'object' ? field : null;
  const hasTemplateItems = Array.isArray(schemaField?.checklistItems)
    && schemaField.checklistItems.length > 0;
  // Templated fields: customs only when explicitly enabled. Freeform (no template): allow add by default.
  const allowCustomItems = hasTemplateItems
    ? schemaField?.allowCustomItems === true
    : schemaField?.allowCustomItems !== false;

  const [checklist, setChecklist] = useState(null);
  const [isEnsuring, setIsEnsuring] = useState(false);
  const ensureAttemptedRef = useRef('');
  const syncAttemptedRef = useRef('');

  const resolvedFieldLabel = String(fieldLabel || 'Checklist').replace(/\s*\*\s*$/, '').trim() || 'Checklist';

  useEffect(() => {
    // RO still subscribes when base is available so previewMode can show
    // live item status; visitors without access fall back to `value` summary.
    if (!activeBase?.key || !node?.key || !fieldName) {
      setChecklist(null);
      return undefined;
    }

    return subscribeToChecklistForField({
      baseKey: activeBase.key,
      nodeKey: node.key,
      fieldName,
    }, (nextChecklist) => {
      setChecklist(nextChecklist || null);
    });
  }, [activeBase?.key, fieldName, node?.key]);

  // Ensure the field always has exactly one list document (empty until items are added).
  useEffect(() => {
    if (isReadOnly) return;
    if (!activeBase?.key || !node?.key || !user?.key || !fieldName) return;
    if (checklist?.key) {
      ensureAttemptedRef.current = '';
      return;
    }

    const attemptKey = `${node.key}:${fieldName}`;
    if (ensureAttemptedRef.current === attemptKey || isEnsuring) return;

    ensureAttemptedRef.current = attemptKey;
    setIsEnsuring(true);

    ensureChecklistForField({
      baseKey: activeBase.key,
      userKey: user.key,
      actor: user,
      nodeKey: node.key,
      nodeType: node.type || null,
      nodeLabel: node.title || node.name || node.key,
      fieldName,
      fieldLabel: resolvedFieldLabel,
      field: schemaField,
    })
      .catch((error) => {
        console.error('[NodeChecklistField] ensure checklist error:', error);
        ensureAttemptedRef.current = '';
      })
      .finally(() => {
        setIsEnsuring(false);
      });
  }, [
    activeBase?.key,
    checklist?.key,
    fieldName,
    isEnsuring,
    isReadOnly,
    node?.key,
    node?.name,
    node?.title,
    node?.type,
    resolvedFieldLabel,
    schemaField,
    user,
  ]);

  // Lazy-sync template items when the checklist doc already exists.
  useEffect(() => {
    if (isReadOnly) return;
    if (!activeBase?.key || !node?.key || !user?.key || !fieldName) return;
    if (!checklist?.key || !schemaField) return;
    if (!Array.isArray(schemaField.checklistItems) || !schemaField.checklistItems.length) return;

    const templateSignature = JSON.stringify(
      (schemaField.checklistItems || []).map((item) => [
        item?.key || item?.value || '',
        item?.translations?.en?.label || item?.label || '',
        item?.sortOrder,
      ]),
    );
    const attemptKey = `${checklist.key}:${templateSignature}`;
    if (syncAttemptedRef.current === attemptKey) return;
    syncAttemptedRef.current = attemptKey;

    ensureChecklistForField({
      baseKey: activeBase.key,
      userKey: user.key,
      actor: user,
      nodeKey: node.key,
      nodeType: node.type || null,
      nodeLabel: node.title || node.name || node.key,
      fieldName,
      fieldLabel: resolvedFieldLabel,
      field: schemaField,
    }).catch((error) => {
      console.error('[NodeChecklistField] sync checklist template error:', error);
      syncAttemptedRef.current = '';
    });
  }, [
    activeBase?.key,
    checklist?.key,
    fieldName,
    isReadOnly,
    node?.key,
    node?.name,
    node?.title,
    node?.type,
    resolvedFieldLabel,
    schemaField,
    user,
  ]);

  const editor = useChecklistInlineEditor({
    checklist,
    user,
    enabled: Boolean(checklist) && !isReadOnly,
  });

  // When RO and we have a live checklist, still mirror items for display without enable.
  const readOnlyLiveItems = useMemo(() => {
    if (!isReadOnly || !checklist?.key) return null;
    const items = checklist?.sourceSnapshot?.items;
    return Array.isArray(items) ? items.filter((item) => String(item?.text || '').trim()) : [];
  }, [checklist, isReadOnly]);

  const summaryFallbackItems = useMemo(() => {
    if (!isReadOnly || checklist?.key) return null;
    const texts = Array.isArray(value?.itemTexts) ? value.itemTexts : [];
    return texts
      .map((text) => String(text || '').trim())
      .filter(Boolean)
      .map((text, index) => ({
        key: `summary-${index}`,
        text,
        status: 'open',
      }));
  }, [checklist?.key, isReadOnly, value?.itemTexts]);

  const displayEditor = useMemo(() => {
    if (!isReadOnly) return editor;
    if (readOnlyLiveItems) {
      return {
        ...editor,
        items: readOnlyLiveItems,
      };
    }
    if (summaryFallbackItems) {
      return {
        ...editor,
        items: summaryFallbackItems,
      };
    }
    return editor;
  }, [editor, isReadOnly, readOnlyLiveItems, summaryFallbackItems]);

  const templateKeys = useMemo(() => (
    normalizeChecklistTemplateItems(schemaField?.checklistItems).map((item) => item.key)
  ), [schemaField?.checklistItems]);

  const progress = useMemo(() => {
    if (isReadOnly && !checklist?.key && value?.kind === 'checklist') {
      const texts = Array.isArray(value?.itemTexts)
        ? value.itemTexts.map((entry) => String(entry || '').trim()).filter(Boolean)
        : [];
      return {
        totalItems: Number(value?.totalItems) || texts.length,
        completedItems: Number(value?.completedItems) || 0,
        items: texts.map((text, index) => ({ key: `summary-${index}`, text, status: 'open' })),
      };
    }

    const sourceItems = Array.isArray(displayEditor.items)
      ? displayEditor.items.filter((item) => String(item?.text || '').trim())
      : [];
    const computed = computeChecklistProgress(sourceItems, { templateKeys });

    return {
      totalItems: computed.totalItems,
      completedItems: computed.completedItems,
      items: sourceItems,
    };
  }, [checklist?.key, displayEditor.items, isReadOnly, templateKeys, value]);

  const progressRatio = progress.totalItems > 0
    ? Math.min(1, progress.completedItems / progress.totalItems)
    : 0;

  const hasDisplaySurface = Boolean(
    checklist
    || (isReadOnly && (summaryFallbackItems?.length || progress.totalItems > 0))
  );

  // Denormalize progress + item preview onto the node for list/gallery display.
  useEffect(() => {
    if (isReadOnly) return;
    if (typeof onChange !== 'function') return;
    if (!checklist?.key) return;

    const summary = buildChecklistProgressSummary(progress, progress.items);
    const currentTexts = Array.isArray(value?.itemTexts)
      ? value.itemTexts.map((entry) => String(entry || '').trim()).filter(Boolean)
      : [];
    const nextTexts = Array.isArray(summary.itemTexts) ? summary.itemTexts : [];
    const sameTexts = currentTexts.length === nextTexts.length
      && currentTexts.every((text, index) => text === nextTexts[index]);
    const currentCompletedKeys = Array.isArray(value?.completedKeys)
      ? value.completedKeys.map((entry) => String(entry || '').trim()).filter(Boolean)
      : [];
    const nextCompletedKeys = Array.isArray(summary.completedKeys) ? summary.completedKeys : [];
    const sameCompletedKeys = currentCompletedKeys.length === nextCompletedKeys.length
      && currentCompletedKeys.every((key, index) => key === nextCompletedKeys[index]);

    if (
      value?.kind === 'checklist'
      && Number(value?.totalItems) === summary.totalItems
      && Number(value?.completedItems) === summary.completedItems
      && sameTexts
      && sameCompletedKeys
    ) {
      return;
    }

    // Empty lists: clear any stale progress summary on the node.
    if (summary.totalItems === 0) {
      if (value == null || value === '' || (value?.kind === 'checklist' && Number(value?.totalItems) === 0)) {
        return;
      }
      onChange(null);
      return;
    }

    onChange(summary);
  }, [
    checklist?.key,
    isReadOnly,
    onChange,
    progress,
    value,
  ]);

  return (
    <div className={`node-checklist-field-container${isReadOnly ? ' is-readonly' : ''}`}>
      <div className="node-checklist-field-input">
        <div className="node-checklist-field-content">
          {hasDisplaySurface ? (
            <ChecklistAccordionPanel
              allowCustomItems={allowCustomItems}
              editor={displayEditor}
              hideItemStatus={Boolean(isReadOnly && !checklist?.key)}
              readOnly={isReadOnly}
            />
          ) : (
            <div
              className="node-checklist-field-empty"
              style={{ opacity: 0.5 }}
            >
              {isReadOnly ? 'No items yet.' : (isEnsuring ? 'Preparing checklist…' : 'No items yet.')}
            </div>
          )}

          {progress.totalItems > 0 ? (
            <div className="node-checklist-field-progress" aria-label={`${progress.completedItems} of ${progress.totalItems} complete`}>
              <div className="node-checklist-field-progress-meta">
                <span
                  className="node-checklist-field-progress-count"
                  style={{ color: theme.foregroundMuted }}
                >
                  {progress.completedItems}/{progress.totalItems}
                </span>
              </div>
              <div
                className="node-checklist-field-progress-track"
                style={{ backgroundColor: 'var(--backgroundHover)' }}
              >
                <div
                  className="node-checklist-field-progress-fill"
                  style={{
                    width: `${Math.round(progressRatio * 100)}%`,
                    backgroundColor: 'var(--foregroundMuted)',
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

NodeChecklistField.displayName = 'NodeChecklistField';

export default NodeChecklistField;
